import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquareHeart, Bug, Lightbulb, Sparkles, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "bug", l: "Bug", Icon: Bug },
  { v: "feature", l: "Feature idea", Icon: Lightbulb },
  { v: "general", l: "General", Icon: MessageSquareHeart },
  { v: "praise", l: "Praise", Icon: Sparkles },
];

export default function FeedbackPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 4) { toast.error("Tell us a little more"); return; }
    setSending(true);
    try {
      await api.post("/feedback", { category, message: message.trim(), contact: contact.trim() || null, anonymous: !contact.trim() });
      setSent(true);
      setMessage(""); setContact("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="feedback-page">
      <Navbar />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-10 py-10">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">/ feedback</div>
        <h1 className="font-heading mt-2">Talk to the team</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Spot a bug, miss a feature, or want to drop a kind word? We read every message. By default your submission is anonymous — only what you type below is sent.
        </p>

        {sent ? (
          <div className="mt-8 rounded-2xl border border-border bg-card/60 p-8 text-center" data-testid="feedback-sent">
            <div className="h-12 w-12 mx-auto rounded-full brand-bg text-black flex items-center justify-center">
              <Check className="h-6 w-6" strokeWidth={2} />
            </div>
            <h2 className="font-heading text-2xl mt-4">Thanks for writing in</h2>
            <p className="text-sm text-muted-foreground mt-2">Your note is in our inbox. We&apos;ll review it shortly.</p>
            <div className="flex gap-2 justify-center mt-5">
              <Button onClick={() => setSent(false)} variant="outline" className="border-border h-11" data-testid="feedback-another">Send another</Button>
              <Button onClick={() => nav("/dashboard")} className="brand-bg text-black hover:opacity-90 h-11">Back to dashboard</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Category</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CATEGORIES.map(({ v, l, Icon }) => (
                  <button
                    key={v} type="button" onClick={() => setCategory(v)}
                    className={`flex items-center justify-center gap-2 h-12 rounded-lg border text-sm transition ${category === v ? "border-[var(--brand)] bg-foreground/5 text-foreground" : "border-border text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]"}`}
                    data-testid={`feedback-cat-${v}`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                    <span>{l}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Message</Label>
              <Textarea
                required value={message} onChange={(e) => setMessage(e.target.value)}
                rows={7} maxLength={4000}
                className="mt-1.5 bg-muted border-border text-base"
                placeholder="What did you run into? What would help?"
                data-testid="feedback-message"
              />
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1 text-right">{message.length} / 4000</div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Reply contact <span className="text-foreground/30">(optional)</span></Label>
              <Input value={contact} onChange={(e) => setContact(e.target.value)} className="mt-1.5 bg-muted border-border h-11 text-base" placeholder="you@email · @handle · leave blank to stay anonymous" data-testid="feedback-contact" />
              <p className="text-[11px] text-muted-foreground mt-1.5">We never include your account email automatically. Leave blank and we cannot reach back out.</p>
            </div>
            <Button type="submit" disabled={sending} className="brand-bg text-black hover:opacity-90 h-12 w-full" data-testid="feedback-submit">
              {sending ? "Sending…" : "Send to developer"}
            </Button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
